
import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  Settings, 
  BarChart3, 
  CreditCard, 
  AlertCircle,
  Activity,
  Database,
  Bell,
  Shield,
  Crown,
  DollarSign,
  ShoppingBag,
  Mail,
  TrendingUp,
  Sparkles,
  Clapperboard
} from "lucide-react";
import { Navigate } from "react-router-dom";
import AdminUsersList from "@/components/admin/AdminUsersList";
import AdminAffiliatesList from "@/components/admin/AdminAffiliatesList";
import AdminStatistics from "@/components/admin/AdminStatistics";
import AdminPayments from "@/components/admin/AdminPayments";
import AdminSettings from "@/components/admin/AdminSettings";
import AdminSystemControl from "@/components/admin/AdminSystemControl";
import { AdminProductsManager } from "@/components/admin/AdminProductsManager";
import AdminNovidades from "@/components/admin/AdminNovidades";
import { AdminNotifications } from "@/components/admin/AdminNotifications";
import AdminEmailDashboard from "@/components/admin/AdminEmailDashboard";
import RealMetricsCards from "@/components/admin/RealMetricsCards";
import RealUserChart from "@/components/admin/charts/RealUserChart";
import RealPlanDistribution from "@/components/admin/charts/RealPlanDistribution";
import AnalyticsConversao from "@/components/admin/analytics/AnalyticsConversao";
import ContentGeneratorPanel from "@/components/admin/content-generator/ContentGeneratorPanel";

const AdminPage = () => {
  const { user, isLoading } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user || user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  const getUserDisplayName = () => {
    if (!user) return "Admin";
    
    // Use the firstName property from AppUser
    if (user.firstName) return user.firstName;
    if (user.email) return user.email.split("@")[0];
    
    return "Admin";
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Crown className="w-8 h-8 text-gold-500" />
              <h1 className="text-4xl font-bold bg-gradient-to-r from-gold-500 to-yellow-500 bg-clip-text text-transparent">
                Painel de Administração SUPREMO
              </h1>
            </div>
            <p className="text-lg text-muted-foreground mt-2 flex items-center gap-2">
              <Shield className="w-5 h-5 text-gold-500" />
              Bem-vindo, {getUserDisplayName()} - Acesso Total Liberado
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="default" className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-500">
              <AlertCircle className="w-4 h-4 mr-2" />
              Sistema Online - Admin Supremo
            </Badge>
            <Badge variant="secondary" className="px-3 py-1 bg-gradient-to-r from-gold-500 to-yellow-500 text-black">
              <Crown className="w-4 h-4 mr-2" />
              Permissões Ilimitadas
            </Badge>
          </div>
        </div>

        {/* Admin Supreme Access Banner */}
        <div className="bg-gradient-to-r from-gold-500/20 to-yellow-500/20 border-2 border-gold-500 rounded-xl p-6 mb-8 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gold-700 dark:text-gold-300 mb-2">
                🏆 ACESSO ADMINISTRATIVO SUPREMO ATIVADO
              </h3>
              <p className="text-gold-600 dark:text-gold-400">
                Todas as funcionalidades, planos e recursos estão liberados para sua conta.
                Você tem controle total sobre o sistema e dados reais.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-12 h-12 text-gold-500" />
              <Crown className="w-12 h-12 text-gold-500" />
            </div>
          </div>
        </div>

        {/* Real-time metrics cards */}
        <RealMetricsCards />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:grid-cols-12">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Visão Geral</span>
            </TabsTrigger>
            <TabsTrigger value="conversao" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              <span className="hidden sm:inline">Conversão</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Usuários</span>
            </TabsTrigger>
            <TabsTrigger value="affiliates" className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              <span className="hidden sm:inline">Afiliados</span>
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Pagamentos</span>
            </TabsTrigger>
            <TabsTrigger value="emails" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">Emails</span>
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden sm:inline">Loja</span>
            </TabsTrigger>
            <TabsTrigger value="system" className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              <span className="hidden sm:inline">Sistema</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              <span className="hidden sm:inline">Avisos</span>
            </TabsTrigger>
            <TabsTrigger value="novidades" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              <span className="hidden sm:inline">Novidades</span>
            </TabsTrigger>
            <TabsTrigger value="content-gen" className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">Conteúdo</span>
            </TabsTrigger>
            <TabsTrigger value="palpites-show" className="flex items-center gap-2">
              <Clapperboard className="w-4 h-4" />
              <span className="hidden sm:inline">Palpites Show</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Configurações</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RealUserChart />
              <RealPlanDistribution />
            </div>
            <AdminStatistics />
          </TabsContent>

          <TabsContent value="conversao" className="space-y-6">
            <AnalyticsConversao />
          </TabsContent>

          <TabsContent value="users" className="space-y-6">
            <AdminUsersList />
          </TabsContent>

          <TabsContent value="affiliates" className="space-y-6">
            <AdminAffiliatesList />
          </TabsContent>

          <TabsContent value="payments" className="space-y-6">
            <AdminPayments />
          </TabsContent>

          <TabsContent value="emails" className="space-y-6">
            <AdminEmailDashboard />
          </TabsContent>

          <TabsContent value="products" className="space-y-6">
            <AdminProductsManager />
          </TabsContent>

          <TabsContent value="system" className="space-y-6">
            <AdminSystemControl />
          </TabsContent>

          <TabsContent value="notifications" className="space-y-6">
            <AdminNotifications />
          </TabsContent>

          <TabsContent value="novidades" className="space-y-6">
            <AdminNovidades />
          </TabsContent>

          <TabsContent value="content-gen" className="space-y-6">
            <ContentGeneratorPanel />
          </TabsContent>


          <TabsContent value="palpites-show" className="space-y-6">
            <Card className="border-2 border-gold-500/40 bg-gradient-to-br from-background to-gold-500/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clapperboard className="w-6 h-6 text-gold-500" />
                  Palpites Show — Modo Apresentação
                </CardTitle>
                <CardDescription>
                  Página exclusiva do admin para gravar os vídeos do canal: slides em fonte gigante,
                  fotos dos bichos corretas e geração de imagem 16:9 (capa YouTube) com 1 clique.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-3">
                <Button
                  onClick={() => window.open("/admin/palpites-show", "_blank")}
                  className="bg-gold-500 hover:bg-gold-600 text-black font-bold"
                >
                  <Clapperboard className="w-4 h-4 mr-2" />
                  Abrir Palpites Show
                </Button>
                <Button variant="outline" onClick={() => (window.location.href = "/admin/palpites-show")}>
                  Abrir nesta aba
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  <Crown className="w-5 h-5 text-gold-500" />
                  Configurações Gerais do Sistema - Admin Supremo
                </CardTitle>
                <CardDescription>
                  Configurações que afetam todo o sistema - Alterações refletem imediatamente
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg">Sistema Principal</h4>
                    <div className="space-y-2">
                      <Button className="w-full justify-between bg-green-500 hover:bg-green-600">
                        Sistema IA
                        <span className="text-xs bg-green-700 px-2 py-1 rounded">ATIVO</span>
                      </Button>
                      <Button className="w-full justify-between bg-green-500 hover:bg-green-600">
                        Geração Palpites
                        <span className="text-xs bg-green-700 px-2 py-1 rounded">ATIVO</span>
                      </Button>
                      <Button className="w-full justify-between bg-green-500 hover:bg-green-600">
                        Sistema Pagamentos
                        <span className="text-xs bg-green-700 px-2 py-1 rounded">ATIVO</span>
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg">Funcionalidades</h4>
                    <div className="space-y-2">
                      <Button className="w-full justify-between bg-green-500 hover:bg-green-600">
                        Software Turbinado
                        <span className="text-xs bg-green-700 px-2 py-1 rounded">ATIVO</span>
                      </Button>
                      <Button className="w-full justify-between bg-green-500 hover:bg-green-600">
                        Afiliados
                        <span className="text-xs bg-green-700 px-2 py-1 rounded">ATIVO</span>
                      </Button>
                      <Button className="w-full justify-between bg-green-500 hover:bg-green-600">
                        Suporte
                        <span className="text-xs bg-green-700 px-2 py-1 rounded">ATIVO</span>
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg">Acesso Premium</h4>
                    <div className="space-y-2">
                      <Button className="w-full justify-between bg-gold-500 hover:bg-gold-600 text-black">
                        Todos os Planos
                        <span className="text-xs bg-gold-700 px-2 py-1 rounded text-white">LIBERADO</span>
                      </Button>
                      <Button className="w-full justify-between bg-gold-500 hover:bg-gold-600 text-black">
                        Recursos VIP
                        <span className="text-xs bg-gold-700 px-2 py-1 rounded text-white">LIBERADO</span>
                      </Button>
                      <Button className="w-full justify-between bg-gold-500 hover:bg-gold-600 text-black">
                        Acesso Ilimitado
                        <span className="text-xs bg-gold-700 px-2 py-1 rounded text-white">LIBERADO</span>
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t">
                  <h4 className="font-semibold text-lg mb-4">Configurações Avançadas</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Button className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 h-12">
                      🔧 Configurar APIs IA
                    </Button>
                    <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 h-12">
                      💳 Configurar Stripe
                    </Button>
                    <Button className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 h-12">
                      📧 Configurar Email
                    </Button>
                    <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 h-12">
                      🗃️ Backup Sistema
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            <AdminSettings />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
